import {
  Avatar,
  Box,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useEffect, useState } from 'react';

export interface ConfirmAiNoteReadModalProps {
  opened: boolean;
  profileDisplayName?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Confirm-read gate after AI draft — keeps name input state local to avoid re-rendering the appointment page. */
export function ConfirmAiNoteReadModal({
  opened,
  profileDisplayName,
  onConfirm,
  onCancel,
}: ConfirmAiNoteReadModalProps): JSX.Element {
  const [nameInput, setNameInput] = useState('');

  useEffect(() => {
    if (opened) setNameInput('');
  }, [opened]);

  const expectedName = profileDisplayName?.trim() ?? '';
  const canConfirm = expectedName.length > 0 && nameInput.trim() === expectedName;

  return (
    <Modal
      opened={opened}
      onClose={onCancel}
      size="sm"
      centered
      withCloseButton={false}
      styles={{
        content: {
          background: 'linear-gradient(160deg, var(--unify-bg-warm) 0%, var(--unify-bg-sidebar) 100%)',
        },
      }}
    >
      <Stack gap="md" align="center" py="sm">
        <Avatar
          src="/Siggie.png"
          size={56}
          radius="xl"
          alt="Siggie"
          style={{
            border: '2px solid var(--mantine-color-gold-3)',
            boxShadow: '0 2px 12px var(--mantine-color-gold-2)',
          }}
        />
        <Stack gap={6} align="center">
          <Text fw={600} size="lg" c="gray.8" ta="center">
            Confirm you&apos;ve reviewed the note
          </Text>
          <Text size="sm" c="gray.6" ta="center" lh={1.6} maw={320}>
            Have you read through the entire note? Type your full name below to save.
          </Text>
          {profileDisplayName ? (
            <Text size="xs" c="gray.5" ta="center">
              Enter exactly: <strong>{profileDisplayName}</strong>
            </Text>
          ) : null}
        </Stack>

        <Box
          p="sm"
          style={{
            borderRadius: 'var(--mantine-radius-md)',
            background: 'var(--mantine-color-gold-0)',
            border: '1px solid var(--mantine-color-gold-2)',
            width: '100%',
          }}
        >
          <Text size="xs" c="gray.6" ta="center" lh={1.5}>
            Signing or saving confirms that you&apos;ve reviewed the note and it accurately reflects the session.
          </Text>
        </Box>

        <TextInput
          label="Your full name"
          placeholder={profileDisplayName ?? 'Type your full name to confirm'}
          value={nameInput}
          onChange={(e) => setNameInput(e.currentTarget.value)}
          size="md"
          style={{ width: '100%' }}
          data-autofocus
        />
        <Group gap="sm" justify="center" w="100%">
          <Button variant="default" onClick={onCancel}>
            Cancel
          </Button>
          <Button color="gold" onClick={onConfirm} disabled={!canConfirm}>
            Confirm and save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
